import { createContext, useContext, useReducer, useEffect } from "react";
import { HomeIcon } from "@heroicons/react/24/outline";
import { getConnectionStatus, onConnectionStatusChange } from "../services/socket";

export const ChatContext = createContext();

export const ChatContextProvider = ({ children }) => {
 
  const INITIAL_STATE = {
    channelId: "null",
    customer: "Customer",
    accountNo: 0,
    user: {},
    accountData: {
      "account": {},
      "balance":0,
      "balance_data":{},
      "comments": [],
      "connection_status": 0,
      "location": [],
      "transactions": []
    },
    isAccountDataLoading: false,
    _accountDataVersion: 0,
    categoryId: 1,
    activeChats: [],
    lastMessages: [],
    currentChat: {"channel_id": 0},
    navigation: [{ name: "Home", action: "SHOW_ADDRESS", icon: HomeIcon }],
    tab: "Home",
    socketConnectionStatus: getConnectionStatus(),
    receivedMessage: null, // For broadcasting new messages to MessagesDashboard
  };

  const chatReducer = (state, action) => { 
    switch (action.type) {
      case "CHANGE_CHAT":
        // When changing chat, clear unread count for that channel
        const clearedUnreadMessages = state.lastMessages.map(chat => {
          if (String(chat.channel_id) === String(action.payload)) {
            return { ...chat, unread: 0 };
          }
          return chat;
        });
        return { 
          ...state,
          channelId: action.payload,
          lastMessages: clearedUnreadMessages
        }; 
      case "SET_CATEGORY":
        return { 
          ...state,
          categoryId: action.payload
        };
      case "SET_ACTIVE_CHATS":
        // Deduplicate active chats based on channel_id to prevent key errors
        const uniqueActiveChats = action.payload.reduce((acc, current) => {
          const x = acc.find(item => item.channel_id === current.channel_id);
          if (!x) {
            return acc.concat([current]);
          } else {
            return acc;
          }
        }, []);
        return { 
          ...state,
          activeChats: uniqueActiveChats
        };
      case "SET_LAST_MESSAGES":
        return { 
          ...state,
          lastMessages: action.payload
        };
      case "SET_CHAT":
        // Extract account number from the chat object
        const chatAccountNo = action.payload?.account_no;
        const accountNo = chatAccountNo !== undefined ? chatAccountNo : 0;
        
        // Check if it's a valid "921" account for loading
        const shouldLoad = accountNo && 
                          accountNo.toString().length >= 7 && 
                          accountNo.toString().startsWith("921");
        
        // Check if switching to a different account
        const isDifferentAccount = accountNo !== state.accountNo;
        
        return { 
          ...state,
          currentChat: action.payload,
          accountNo: accountNo,
          isAccountDataLoading: shouldLoad,
          // Reset account data AND navigation when switching chats
          accountData: isDifferentAccount ? INITIAL_STATE.accountData : state.accountData,
          navigation: isDifferentAccount ? INITIAL_STATE.navigation : state.navigation,
          tab: isDifferentAccount ? "Home" : state.tab
        };
      case "SET_ACCOUNT_NO":
        // When account number changes, trigger loading if it's a valid "921" account
        const newAccountNo = action.payload;
        const shouldTriggerLoading = newAccountNo && 
                                      newAccountNo.toString().length >= 7 && 
                                      newAccountNo.toString().startsWith("921");
        return { 
          ...state,
          accountNo: newAccountNo,
          isAccountDataLoading: shouldTriggerLoading,
          // Reset account data when account number changes
          accountData: newAccountNo !== state.accountNo ? INITIAL_STATE.accountData : state.accountData
        };
      case "SET_ACCOUNT_DATA":
        // Only update if the fetched data is for the currently active accountNo in the state.
        if (state.accountNo === action.payload.fetchedForAccountNo) {
          return { 
            ...state,
            accountData: action.payload.fetchedData,
            isAccountDataLoading: false,
            _accountDataVersion: state._accountDataVersion + 1
          };
        } else {
          console.warn(`SET_ACCOUNT_DATA: Stale data received for account ${action.payload.fetchedForAccountNo}, but current account is ${state.accountNo}. Triggering fresh load.`);
          // Trigger loading for the current account
          return {
            ...state,
            isAccountDataLoading: true
          };
        }
      case "SET_NAVIGATION":
        return { 
          ...state,
          navigation: action.payload
        };
      case "SET_TAB":
        return { 
          ...state,
          tab: action.payload
        };
      case "SET_USER":
        return { 
          ...state,
          user: action.payload
        };
      case "SET_CUSTOMER":
        return { 
          ...state,
          customer: action.payload
        };
      case "START_ACCOUNT_DATA_LOADING":
        return { 
          ...state,
          isAccountDataLoading: true
        };
      case "RESET_ACCOUNT_VIEW":
        return { 
          ...state,
          accountData: INITIAL_STATE.accountData,
          tab: "Home",
          navigation: INITIAL_STATE.navigation,
          isAccountDataLoading: true  // Set to true to trigger account data fetch
        };
      case "FINISH_ACCOUNT_LOADING_WITHOUT_DATA":
        return { 
          ...state,
          isAccountDataLoading: false
        };
      case "SET_SOCKET_CONNECTION_STATUS":
        return { 
          ...state,
          socketConnectionStatus: action.payload
        };
      case "RECEIVE_MESSAGE":
        return { 
          ...state,
          receivedMessage: action.payload
        };
      case "UPDATE_LAST_MESSAGE":
        // Update the lastMessages array with new message info
        const updatedLastMessages = state.lastMessages.map(chat => {
          if (String(chat.channel_id) === String(action.payload.channel_id)) {
            return {
              ...chat,
              name: action.payload.name || chat.name,
              message: action.payload.message,
              timestamp: action.payload.timestamp,
              unread: action.payload.unread !== undefined ? action.payload.unread : chat.unread
            };
          }
          return chat;
        });
        
        // If channel doesn't exist in lastMessages, add it
        const channelExists = state.lastMessages.some(
          chat => String(chat.channel_id) === String(action.payload.channel_id)
        );
        
        if (!channelExists) {
          updatedLastMessages.push({
            channel_id: action.payload.channel_id,
            name: action.payload.name || 'Unknown',
            message: action.payload.message,
            timestamp: action.payload.timestamp,
            unread: action.payload.unread || 0
          });
        }
        
        return {
          ...state,
          lastMessages: updatedLastMessages,
          // Keep activeChats in sync with lastMessages so the carousel shows new/unread chats
          activeChats: (() => {
            try {
              const clone = updatedLastMessages.slice();
              // Prioritize unread incoming chats first, then most recent
              clone.sort((a, b) => {
                const unreadA = (a.unread || 0) > 0 && a.direction === 0;
                const unreadB = (b.unread || 0) > 0 && b.direction === 0;
                if (unreadA && !unreadB) return -1;
                if (!unreadA && unreadB) return 1;
                return (b.timestamp || 0) - (a.timestamp || 0);
              });
              return clone;
            } catch (err) {
              return state.activeChats || [];
            }
          })()
        };
      default:
        return state;
    }
  };

  const [data, dispatch] = useReducer(chatReducer, INITIAL_STATE);

  // Set up socket connection status listener
  useEffect(() => {
    const unsubscribe = onConnectionStatusChange((status) => {
      dispatch({ type: "SET_SOCKET_CONNECTION_STATUS", payload: status });
    });

    return unsubscribe;
  }, []);

  return (
    <ChatContext.Provider value={{ data, dispatch }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatContextProvider");
  }
  return context;
};
